(() => {
  "use strict";

  const PRODUCT_FORM_ELEMENT = "component-product-form";
  const QUICK_CART_OPEN_EVENT = "theme-quickCart:open";
  const ADD_REQUEST_TIMEOUT_MS = 15000;
  const QUICK_CART_TIMEOUT_MS = 10000;
  const CART_REDIRECT_DELAY_MS = 900;

  const messages = {
    addNotConfirmed:
      "No pudimos confirmar que el producto se agregara. Revisa tu carrito antes de volver a intentarlo.",
    drawerNotConfirmed:
      "El producto sí se agregó. Abriremos tu carrito para que puedas continuar.",
    genericAddError:
      "No pudimos agregar el producto. Revisa las opciones seleccionadas e inténtalo nuevamente.",
  };

  const delay = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  async function requestAddToCart(url, options) {
    const AbortControllerClass = window.AbortController;
    const controller =
      typeof AbortControllerClass === "function"
        ? new AbortControllerClass()
        : null;
    let timeoutId;

    const timeout = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => {
        controller?.abort();
        reject(new Error("Add-to-cart request timed out"));
      }, ADD_REQUEST_TIMEOUT_MS);
    });

    const request = (async () => {
      const response = await fetch(url, {
        ...options,
        ...(controller ? { signal: controller.signal } : {}),
      });

      let payload;
      try {
        payload = await response.json();
      } catch (error) {
        if (response.ok) {
          payload = {};
        } else {
          throw new Error("Invalid add-to-cart response", { cause: error });
        }
      }

      return { payload, response };
    })();

    try {
      return await Promise.race([request, timeout]);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  function getErrorMessage(payload) {
    if (!payload || typeof payload !== "object") return messages.genericAddError;

    if (typeof payload.description === "string" && payload.description.trim()) {
      return payload.description;
    }

    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }

    if (typeof payload.errors === "string" && payload.errors.trim()) {
      return payload.errors;
    }

    if (payload.errors && typeof payload.errors === "object") {
      const firstError = Object.values(payload.errors).flat().find(Boolean);
      if (typeof firstError === "string") return firstError;
    }

    return messages.genericAddError;
  }

  function setLoading(productForm, isLoading) {
    const currentButton =
      productForm.querySelector?.(
        productForm.selectors?.submit || ".product-form__submit",
      ) || productForm.submitButton;

    if (!currentButton) return;

    productForm.submitButton = currentButton;

    currentButton.classList.toggle("active", isLoading);

    if (isLoading) {
      currentButton.setAttribute("aria-busy", "true");
    } else {
      currentButton.removeAttribute("aria-busy");
    }
  }

  function clearError(productForm) {
    productForm.errorContainer?.classList.remove("active");
    if (productForm.errorMessage) productForm.errorMessage.textContent = "";
  }

  function showError(productForm, message) {
    if (!productForm.errorContainer || !productForm.errorMessage) return;

    productForm.errorContainer.setAttribute("role", "alert");
    productForm.errorContainer.setAttribute("aria-live", "assertive");
    productForm.errorContainer.setAttribute("aria-atomic", "true");
    productForm.errorMessage.textContent = message;
    productForm.errorContainer.classList.add("active");
  }

  function waitForQuickCartOpen(productForm) {
    return new Promise((resolve) => {
      let settled = false;

      const finish = (opened) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        document.removeEventListener(QUICK_CART_OPEN_EVENT, handleOpen);
        resolve(opened);
      };

      const handleOpen = () => {
        Promise.resolve().then(() => {
          const panel = productForm.quickCart?.closest?.("component-panel");
          finish(!panel || panel.classList.contains("active"));
        });
      };
      const timeoutId = window.setTimeout(
        () => finish(false),
        QUICK_CART_TIMEOUT_MS,
      );

      document.addEventListener(QUICK_CART_OPEN_EVENT, handleOpen, { once: true });
    });
  }

  customElements.whenDefined(PRODUCT_FORM_ELEMENT).then(() => {
    const ProductForm = customElements.get(PRODUCT_FORM_ELEMENT);
    const prototype = ProductForm?.prototype;

    if (!prototype || prototype.__zinusCartResiliencePatched) return;

    Object.defineProperty(prototype, "__zinusCartResiliencePatched", {
      configurable: false,
      enumerable: false,
      value: true,
      writable: false,
    });

    prototype.handleFormSubmit = async function handleFormSubmit(event) {
      event.preventDefault();

      if (!this.form) return;

      if (this.__zinusAddInFlight) {
        setLoading(this, true);
        return;
      }

      this.__zinusAddInFlight = true;

      clearError(this);
      setLoading(this, true);

      const cartAddUrl = window.routes?.cart_add_url || "/cart/add";
      const cartUrl = window.routes?.cart_url || "/cart";
      let addWasConfirmed = false;

      try {
        const { payload, response } = await requestAddToCart(cartAddUrl, {
          method: "POST",
          headers: {
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: new FormData(this.form),
        });

        if (!response.ok || payload?.status) {
          const giftCardFields = this.querySelector(
            ".gc-recipient-form__fields.visible",
          );
          const giftCardForm = this.querySelector("component-gc-recipient-form");

          if (giftCardFields && giftCardForm?.showError && payload?.errors) {
            giftCardForm.showError(payload?.errors);
          } else {
            showError(this, getErrorMessage(payload));
          }

          document.dispatchEvent(
            new CustomEvent("theme-cart:updated", {
              detail: { shouldOpenCart: false },
            }),
          );
          return;
        }

        addWasConfirmed = true;

        if (!this.quickCart) {
          const wrappingProduct = this.closest("section-product");

          if (wrappingProduct?.classList.contains("product-quick-add")) {
            document.dispatchEvent(
              new CustomEvent("theme-cart:updated", {
                detail: { shouldOpenCart: false },
              }),
            );
            document.dispatchEvent(new CustomEvent("theme-modal:close"));
          } else {
            window.location.assign(cartUrl);
          }
          return;
        }

        const quickCartOpened = waitForQuickCartOpen(this);

        document.dispatchEvent(
          new CustomEvent("theme-cart:updated", {
            detail: { shouldOpenCart: true },
          }),
        );
        document.dispatchEvent(new CustomEvent("theme-modal:close"));

        if (await quickCartOpened) return;

        showError(this, messages.drawerNotConfirmed);
        await delay(CART_REDIRECT_DELAY_MS);
        window.location.assign(cartUrl);
      } catch (error) {
        console.error("[Zinus cart resilience]", error);
        const message = addWasConfirmed
          ? messages.drawerNotConfirmed
          : messages.addNotConfirmed;

        showError(this, message);

        if (addWasConfirmed) {
          await delay(CART_REDIRECT_DELAY_MS);
          window.location.assign(cartUrl);
        }
      } finally {
        this.__zinusAddInFlight = false;
        setLoading(this, false);
      }
    };
  });
})();
